/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/
import type { FetchGraphQL, setFetchGraphQlHeader } from '@adobe-commerce/fetch-graphql';
import { config } from '@/company-switcher/api';

/**
 * Manages customer group headers for GraphQL requests.
 * Singleton class that handles setting and removing group headers across multiple GraphQL modules.
 */
class GroupHeaderManager {
  private groupHeaderSet: boolean = false;
  private groupHeaderAppliers: typeof setFetchGraphQlHeader[] = [];
  private groupHeaderRemovers: ((key: string) => void)[] = [];
  private headerKey: string;
  private defaultNLICustomerGroupId = 'b6589fc6ab0dc82cf12099d1c2d40ab994e8410c';


  constructor() {
    this.headerKey = config.getConfig().customerGroupHeader;
    this.setFetchGraphQlModules(config.getConfig().groupGraphQlModules);
  }

  /**
   * Sets the header key used for customer group identification
   * @param headerKey - The header name to use for group ID
   */
  public setHeaderKey(headerKey: string): void {
    this.headerKey = headerKey;
  }

  /**
   * Configures GraphQL modules that will have group headers applied
   * @param modules - Array of GraphQL modules with header management functions
   */
  public setFetchGraphQlModules(modules: FetchGraphQL[]): void {
    modules.forEach((module: FetchGraphQL) => {
      this.groupHeaderAppliers.push((key: string, value: string) => 
        module.setFetchGraphQlHeader(key, value)
      );
      this.groupHeaderRemovers.push((key: string) => 
        module.setFetchGraphQlHeader(key, this.defaultNLICustomerGroupId)
      );
    });
  }

  /**
   * Sets customer group headers for all configured GraphQL modules.
   * Always removes existing headers first before setting new ones.
   * @param groupId - The group ID to set in headers, or null to only remove headers
   */
  public setGroupHeaders(groupId: string | null): void {
    this.removeGroupHeaders();
    if (groupId === null) {
      return;
    }
    this.groupHeaderAppliers.forEach((setFn) => {
      setFn(this.headerKey, groupId);
    });
    this.groupHeaderSet = true;
  }

  /**
   * Removes customer group headers from all configured GraphQL modules
   */
  public removeGroupHeaders(): void {
    this.groupHeaderRemovers.forEach((removeFn) => {
      removeFn(this.headerKey);
    });
    this.groupHeaderSet = false;
  }

  /**
   * Checks if customer group headers are currently set
   * @returns true if group headers are set, false otherwise
   */
  public isGroupHeaderSet(): boolean {
    return this.groupHeaderSet;
  }
}

let _groupHeaderManager: GroupHeaderManager | null = null;

/**
 * Gets the singleton instance of GroupHeaderManager
 * @returns The GroupHeaderManager instance
 */
export const getGroupHeaderManager = () => {
  if (!_groupHeaderManager) {
    _groupHeaderManager = new GroupHeaderManager();
  }
  return _groupHeaderManager;
};
